/**
 * Node Editor Component
 * 
 * Provides a form interface for editing OST nodes.
 * Adapts fields based on node type.
 */

import { useState, useEffect, useRef } from 'react';
import {
  Target,
  Lightbulb,
  Wrench,
  FlaskConical,
  X,
  Trash2,
  Crosshair,
  Save,
  Check,
  Link2,
  Link2Off,
  Search,
  ExternalLink,
} from 'lucide-react';
import type { 
  OSTNode, 
  NodeType,
  OutcomeNode,
  OpportunityNode,
  SolutionNode,
  ExperimentNode,
  OpportunitySource,
  ExperimentType,
  ExperimentStatus,
  ValidationStatus,
} from '../types';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import type { Item } from '../../../types/prioritization';

interface NodeEditorProps {
  node: OSTNode | null;
  onSave: (node: OSTNode) => void;
  onDelete: (nodeId: string) => void;
  onClose: () => void;
  onSetTargetOpportunity?: (nodeId: string) => void;
  onAttachItem?: (nodeId: string, itemId: string) => void;
  onDetachItem?: (nodeId: string) => void;
}

const nodeTypeConfig: Record<NodeType, { icon: typeof Target; color: string; label: string }> = {
  outcome: { icon: Target, color: 'text-consulting-slate-blue-700', label: 'Strategy' },
  opportunity: { icon: Lightbulb, color: 'text-[var(--mustard)]', label: 'Opportunity' },
  solution: { icon: Wrench, color: 'text-[var(--text-ter)]', label: 'Solution' },
  experiment: { icon: FlaskConical, color: 'text-[var(--green)]', label: 'Experiment' },
};

const opportunitySources: { value: OpportunitySource; label: string }[] = [
  { value: 'customer_interview', label: 'Customer Interview' },
  { value: 'support_ticket', label: 'Support Ticket' },
  { value: 'sales_conversation', label: 'Sales Conversation' },
  { value: 'analytics', label: 'Analytics' },
  { value: 'survey', label: 'Survey' },
  { value: 'user_research', label: 'User Research' },
  { value: 'assumption', label: 'Assumption' },
];

const experimentTypes: { value: ExperimentType; label: string }[] = [
  { value: 'ab_test', label: 'A/B Test' },
  { value: 'prototype_test', label: 'Prototype Test' },
  { value: 'fake_door', label: 'Fake Door Test' },
  { value: 'concierge', label: 'Concierge MVP' },
  { value: 'wizard_of_oz', label: 'Wizard of Oz' },
  { value: 'landing_page', label: 'Landing Page Test' },
  { value: 'user_interview', label: 'User Interview' },
  { value: 'survey', label: 'Survey' },
  { value: 'mvp', label: 'MVP' },
];

const experimentStatuses: { value: ExperimentStatus; label: string }[] = [
  { value: 'planned', label: 'Planned' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'inconclusive', label: 'Inconclusive' },
];

const validationStatuses: { value: ValidationStatus; label: string }[] = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'validated', label: 'Validated' },
  { value: 'invalidated', label: 'Invalidated' },
  { value: 'needs_more_data', label: 'Needs More Data' },
];

const effortLevels = [
  { value: 'xs', label: 'XS (hours)' },
  { value: 'small', label: 'Small (days)' },
  { value: 'medium', label: 'Medium (1-2 weeks)' },
  { value: 'large', label: 'Large (1+ month)' },
  { value: 'xl', label: 'XL (quarter+)' },
];

// ── Item Link Section ─────────────────────────────────────────────────────────

const CATEGORY_LABEL: Record<string, string> = {
  opportunity: 'opportunity',
  solution: 'solution',
};

function ItemLinkSection({
  nodeId,
  nodeType,
  itemId,
  onAttachItem,
  onDetachItem,
}: {
  nodeId: string;
  nodeType: 'opportunity' | 'solution';
  itemId?: string | null;
  onAttachItem?: (nodeId: string, itemId: string) => void;
  onDetachItem?: (nodeId: string) => void;
}) {
  const allItems = useItemsStore(state => state.items);
  const [showPicker, setShowPicker] = useState(false);
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  // Items with matching category, excluding already-linked ones
  const candidates = allItems.filter(
    (item) =>
      (item as any).itemCategory === nodeType &&
      (item as any).exit_status !== 'archived' &&
      item.id !== itemId
  );

  const filtered = query.trim()
    ? candidates.filter((item) =>
        item.title.toLowerCase().includes(query.toLowerCase()) ||
        (item.description ?? '').toLowerCase().includes(query.toLowerCase())
      )
    : candidates;

  const linkedItem = itemId ? allItems.find((i) => i.id === itemId) : null;

  useEffect(() => {
    if (showPicker) setTimeout(() => searchRef.current?.focus(), 50);
  }, [showPicker]);

  const handleSelect = (item: Item) => {
    onAttachItem?.(nodeId, item.id);
    setShowPicker(false);
    setQuery('');
  };

  return (
    <div className="border border-[var(--rule)] p-3 bg-[var(--surface)]/60 dark:bg-[var(--bg-raised)]/30">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">
          <Link2 className="h-3.5 w-3.5" />
          Linked Item
        </div>
        {linkedItem && onDetachItem && (
          <button
            onClick={() => onDetachItem(nodeId)}
            className="flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--coral)] transition-colors"
            title="Unlink item"
          >
            <Link2Off className="h-3.5 w-3.5" />
            Unlink
          </button>
        )}
      </div>

      {linkedItem ? (
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[var(--text-pri)] truncate">
              {linkedItem.title}
            </p>
            {(linkedItem as any).description && (
              <p className="text-xs text-[var(--text-ter)] line-clamp-2 mt-0.5">
                {(linkedItem as any).description}
              </p>
            )}
          </div>
          <a
            href={`/prioritization/items/${linkedItem.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 text-[var(--text-ter)] hover:text-consulting-slate-blue-600 transition-colors"
            title="Open item"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      ) : (
        <>
          {!showPicker ? (
            <button
              onClick={() => setShowPicker(true)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-[var(--text-ter)] border border-dashed border-[var(--rule)] hover:border-consulting-slate-blue-400 hover:text-consulting-slate-blue-600 transition-colors"
            >
              <Search className="h-3.5 w-3.5" />
              Attach existing {CATEGORY_LABEL[nodeType] ?? nodeType} item…
            </button>
          ) : (
            <div>
              <div className="relative mb-2">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)]" />
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={`Search ${CATEGORY_LABEL[nodeType]} items…`}
                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] focus:outline-none focus:ring-2 focus:ring-consulting-slate-blue-500"
                />
                <button
                  onClick={() => { setShowPicker(false); setQuery(''); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-ter)] hover:text-[var(--text-sec)]"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="max-h-48 overflow-y-auto divide-y divide-[var(--rule)] dark:divide-[var(--rule)] border border-[var(--rule)] bg-[var(--bg-raised)]">
                {filtered.length === 0 ? (
                  <p className="px-3 py-3 text-xs text-[var(--text-ter)] text-center">
                    {candidates.length === 0
                      ? `No ${CATEGORY_LABEL[nodeType]} items in the item list yet.`
                      : 'No matches for your search.'}
                  </p>
                ) : (
                  filtered.slice(0, 30).map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleSelect(item)}
                      className="w-full text-left px-3 py-2 hover:bg-consulting-slate-blue-50 dark:hover:bg-consulting-slate-blue-900/20 transition-colors"
                    >
                      <p className="text-sm font-medium text-[var(--text-pri)] truncate">
                        {item.title}
                      </p>
                      {(item as any).description && (
                        <p className="text-xs text-[var(--text-ter)] truncate mt-0.5">
                          {(item as any).description}
                        </p>
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export function NodeEditor({
  node,
  onSave,
  onDelete,
  onClose,
  onSetTargetOpportunity,
  onAttachItem,
  onDetachItem,
}: NodeEditorProps) {
  const [editedNode, setEditedNode] = useState<OSTNode | null>(node);
  const [isDirty, setIsDirty] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  
  useEffect(() => {
    setEditedNode(node);
    setIsDirty(false);
  }, [node]);
  
  if (!editedNode) {
    return (
      <div className="flex items-center justify-center h-full text-[var(--text-ter)]">
        <p>Select a node to edit</p>
      </div>
    );
  }
  
  const config = nodeTypeConfig[editedNode.type];
  const Icon = config.icon;
  
  const updateField = <K extends keyof OSTNode>(field: K, value: OSTNode[K]) => {
    setEditedNode(prev => {
      if (!prev) return prev;
      return { ...prev, [field]: value, updatedAt: new Date().toISOString() };
    });
    setIsDirty(true);
  };
  
  const handleSave = () => {
    if (editedNode) {
      onSave(editedNode);
      setIsDirty(false);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    }
  };
  
  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this node and all its children?')) {
      onDelete(editedNode.id);
    }
  };
  
  return (
    <div className="h-full flex flex-col bg-[var(--bg-raised)] border-l border-[var(--rule)]">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-[var(--rule)]">
        <div className="flex items-center gap-2">
          <Icon className={`h-5 w-5 ${config.color}`} />
          <span className="font-medium text-[var(--text-pri)]">Edit {config.label}</span>
        </div>
        <button
          onClick={onClose}
          className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] rounded"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      
      {/* Form */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
            Title *
          </label>
          <input
            type="text"
            value={editedNode.title}
            onChange={(e) => updateField('title', e.target.value)}
            className="w-full px-3 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-consulting-slate-blue-500"
            placeholder={`Enter ${config.label.toLowerCase()} title`}
          />
        </div>
        
        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
            Description <span className="font-normal text-[var(--text-ter)]">(optional)</span>
          </label>
          <textarea
            value={editedNode.description || ''}
            onChange={(e) => updateField('description', e.target.value)}
            className="w-full px-3 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-consulting-slate-blue-500 min-h-[80px]"
            placeholder="Add more details..."
          />
        </div>
        
        {/* Outcome-specific fields */}
        {editedNode.type === 'outcome' && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                  Metric
                </label>
                <input
                  type="text"
                  value={(editedNode as OutcomeNode).metric || ''}
                  onChange={(e) => updateField('metric' as any, e.target.value)}
                  className="w-full px-3 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-consulting-slate-blue-500"
                  placeholder="e.g., DAU"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                  Target
                </label>
                <input
                  type="text"
                  value={(editedNode as OutcomeNode).target || ''}
                  onChange={(e) => updateField('target' as any, e.target.value)}
                  className="w-full px-3 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-consulting-slate-blue-500"
                  placeholder="e.g., 50%"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                  Current Value
                </label>
                <input
                  type="text"
                  value={(editedNode as OutcomeNode).currentValue || ''}
                  onChange={(e) => updateField('currentValue' as any, e.target.value)}
                  className="w-full px-3 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-consulting-slate-blue-500"
                  placeholder="e.g., 35%"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                  Timeframe
                </label>
                <input
                  type="text"
                  value={(editedNode as OutcomeNode).timeframe || ''}
                  onChange={(e) => updateField('timeframe' as any, e.target.value)}
                  className="w-full px-3 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-consulting-slate-blue-500"
                  placeholder="e.g., Q1 2026"
                />
              </div>
            </div>
          </>
        )}
        
        {/* Item Link — opportunity and solution nodes only */}
        {(editedNode.type === 'opportunity' || editedNode.type === 'solution') && (
          <ItemLinkSection
            nodeId={editedNode.id}
            nodeType={editedNode.type}
            itemId={editedNode.itemId}
            onAttachItem={onAttachItem}
            onDetachItem={onDetachItem}
          />
        )}

        {/* Opportunity-specific fields */}
        {editedNode.type === 'opportunity' && (
          <>
            {/* Target opportunity toggle */}
            {onSetTargetOpportunity && (
              <div className="p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Crosshair className="h-4 w-4 text-[var(--mustard)]" />
                    <span className="text-sm font-medium text-[var(--mustard)]">
                      Target Opportunity
                    </span>
                  </div>
                  <button
                    onClick={() => onSetTargetOpportunity(editedNode.id)}
                    className={`px-3 py-1 text-sm  transition-colors ${
                      (editedNode as OpportunityNode).isTargeted
                        ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]'
                        : 'bg-[var(--bg-raised)] text-[var(--mustard)] border border-[var(--mustard)] hover:bg-[var(--mustard-dim)]'
                    }`}
                  >
                    {(editedNode as OpportunityNode).isTargeted ? 'Current Target' : 'Set as Target'}
                  </button>
                </div>
                <p className="text-xs text-[var(--mustard)] mt-2">
                  Focus on one opportunity at a time to enable deeper solution exploration.
                </p>
              </div>
            )}
          </>
        )}
        
        {/* Solution-specific fields — additional metadata (effort, confidence,
            assumptions, validation status) are managed in Item detail view. */}
        
        {/* Experiment-specific fields */}
        {editedNode.type === 'experiment' && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                  Experiment Type
                </label>
                <select
                  value={(editedNode as ExperimentNode).experimentType}
                  onChange={(e) => updateField('experimentType' as any, e.target.value as ExperimentType)}
                  className="w-full px-3 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-consulting-slate-blue-500"
                >
                  {experimentTypes.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                  Status
                </label>
                <select
                  value={(editedNode as ExperimentNode).status}
                  onChange={(e) => updateField('status' as any, e.target.value as ExperimentStatus)}
                  className="w-full px-3 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-consulting-slate-blue-500"
                >
                  {experimentStatuses.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                Hypothesis
              </label>
              <textarea
                value={(editedNode as ExperimentNode).hypothesis || ''}
                onChange={(e) => updateField('hypothesis' as any, e.target.value)}
                className="w-full px-3 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-consulting-slate-blue-500"
                placeholder="We believe that..."
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                Success Criteria
              </label>
              <input
                type="text"
                value={(editedNode as ExperimentNode).successCriteria || ''}
                onChange={(e) => updateField('successCriteria' as any, e.target.value)}
                className="w-full px-3 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-consulting-slate-blue-500"
                placeholder="What will prove the hypothesis?"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                Results
              </label>
              <textarea
                value={(editedNode as ExperimentNode).results || ''}
                onChange={(e) => updateField('results' as any, e.target.value)}
                className="w-full px-3 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-consulting-slate-blue-500"
                placeholder="What happened?"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                Learnings
              </label>
              <textarea
                value={(editedNode as ExperimentNode).learnings || ''}
                onChange={(e) => updateField('learnings' as any, e.target.value)}
                className="w-full px-3 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-consulting-slate-blue-500"
                placeholder="What did we learn?"
              />
            </div>
          </>
        )}
      </div>
      
      {/* Footer actions */}
      <div className="p-4 border-t border-[var(--rule)] flex items-center justify-between">
        <button
          onClick={handleDelete}
          className="flex items-center gap-2 px-3 py-2 text-[var(--coral)] hover:bg-[var(--coral-dim)] dark:hover:bg-[var(--coral)]/20 transition-colors"
        >
          <Trash2 className="h-4 w-4" />
          <span>Delete</span>
        </button>
        
        <button
          onClick={handleSave}
          disabled={!isDirty && !savedFlash}
          className={`flex items-center gap-2 px-4 py-2  transition-colors ${
            savedFlash
              ? 'bg-[var(--green)] text-white cursor-default'
              : 'bg-consulting-slate-blue-600 text-white hover:bg-consulting-slate-blue-700 disabled:opacity-40 disabled:cursor-not-allowed'
          }`}
        >
          {savedFlash ? <Check className="h-4 w-4" /> : <Save className="h-4 w-4" />}
          <span>{savedFlash ? 'Saved' : 'Save Changes'}</span>
        </button>
      </div>
    </div>
  );
}

export default NodeEditor;
